import { Button, CardBase, Text, View, ProgressCircle } from '@/components'
import { IconProps } from '@codeleap/web'
import { formatDateLocalized, variantProvider } from '@/app'
import { arePropsEqual, PropsOf, useI18N } from '@codeleap/common'
import React from 'react'
import { Question, Reply } from '@/types'
import { Link } from 'gatsby'
import { TagType } from '../../../../components/Feed/atoms/TagType'
import { TopicTag } from '../atoms/TopicTag'
import { Organisation } from '@/features'

type ReviewCardProps = {
  title: string
  date: string
  text: string
  progress: number
  icon?: IconProps['name']
  replies?: Reply[]
  asLink?: boolean
  href?: string
  question?: Question
  renderWrapper?: ({ children }) => React.ReactElement
  showReadMore?: boolean
} & Partial<PropsOf<typeof CardBase>>

const multiplier = 10
const maxChar = 280

function ReviewCardComponent(props: ReviewCardProps) {
  const {
    title,
    date,
    text,
    progress,
    icon,
    onPress,
    replies,
    asLink = false,
    showReadMore = false,
    href,
    question,
    renderWrapper: Wrapper = ({ children }) => (
      <CardBase
        debugName='Organisation score card'
        variants={[
          'backgroundColor:neutral1',
          'padding:2',
          'border-radius:medium',
          'card:elevated',
          'fullWidth',
          'review',
        ]}
        onPress={onPress}
        component={asLink ? Link : 'div'}
        href={href}
        {...otherProps}
      >
        {children}
      </CardBase>
    ),
    ...otherProps
  } = props

  const { t } = useI18N()
  const showReadMoreBtn = !!text && text?.length > maxChar
  const txt = showReadMoreBtn ? text?.substring(0, maxChar).trim() + '...' : text
  const showReply = replies?.length > 0

  const dateFormatted = formatDateLocalized(new Date(date), 'learn.stories.date_format')

  const isNpsQuestion = !!question?.is_nps

  return (
    <Wrapper>
      <View variants={['fullWidth', 'column', 'gap:1']}>
        <TagType icon='note' />
        <View variants={['fullWidth', 'justifySpaceBetween', 'alignStart', 'gap:1']}>
          <View variants={['column']}>
            <View variants={['flex', 'alignCenter', 'gap:1']}>
              <Text variants={['h5', 'color:neutral6']} text={title} component='h3' />
              <View style={styles.progressWrapper}>
                <ProgressCircle variants={['tiny']} image={icon} progress={progress * multiplier} />
              </View>
            </View>
            <Text variants={['color:neutral5', 'p4']} text={dateFormatted} />
          </View>
        </View>

        <View variants={['column', 'fullWidth']}>
          <Text variants={['p3']} text={txt} style={styles.text} />

          <View variants={['justifySpaceBetween', 'alignCenter', 'marginTop:1.5']}>
            <TopicTag topic={question?.topic} />
            {(showReadMoreBtn || showReadMore) && (
              <Button
                debugName={'Read more'}
                variants={['minimal']}
                text={t('reusable.readMore')}
                styles={{ wrapper: styles.buttonWrapper }}
                onClick={onPress}
                tabIndex={otherProps?.tabIndex + 0.1}
                href={href}
                component={asLink ? Link : undefined}
              />
            )}
          </View>
        </View>

        {showReply && <Organisation.ReplyPreview replies={replies} />}
      </View>
    </Wrapper>
  )
}

const progressSize = 32

const styles = variantProvider.createComponentStyle(
  theme => ({
    text: {
      wordBreak: 'break-word',
    },
    buttonWrapper: {
      height: '24',
      textDecoration: 'none',
      '&:hover': {},
    },
    progressWrapper: {
      maxWidth: progressSize,
      maxHeight: progressSize,
      minWidth: progressSize,
      minHeight: progressSize,
    },
  }),
  true,
)

export const ReviewCard = React.memo(ReviewCardComponent, (previous, next) => {
  return arePropsEqual(previous, next, {
    check: ['title', 'date', 'text', 'progress', 'icon'],
  })
})
